import type { ReactElement } from 'react'

/**
 * One glyph per nav destination, for the collapsed rail (see CadenceShell). Kept to the same
 * plain stroke language throughout — 20x20, currentColor, round caps — rather than a mixed set
 * of styles that would read as several different icon packs stitched together.
 */

const base = {
  width: 18,
  height: 18,
  viewBox: '0 0 20 20',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.6,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
}

export const NAV_ICON: Record<string, ReactElement> = {
  overview: (
    <svg {...base}>
      <rect x="3" y="3" width="6" height="6" rx="1.2" />
      <rect x="11" y="3" width="6" height="6" rx="1.2" />
      <rect x="3" y="11" width="6" height="6" rx="1.2" />
      <rect x="11" y="11" width="6" height="6" rx="1.2" />
    </svg>
  ),
  inbox: (
    <svg {...base}>
      <path d="M3 6.5c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2v6c0 1.1-.9 2-2 2H8l-3.5 3v-3H5c-1.1 0-2-.9-2-2v-6Z" />
    </svg>
  ),
  contacts: (
    <svg {...base}>
      <circle cx="10" cy="7" r="3" />
      <path d="M4 17c0-3 2.7-5 6-5s6 2 6 5" />
    </svg>
  ),
  tasks: (
    <svg {...base}>
      <rect x="3.5" y="3.5" width="13" height="13" rx="2" />
      <path d="M6.5 10.2l2 2 5-5" />
    </svg>
  ),
  notifications: (
    <svg {...base}>
      <path d="M6 8a4 4 0 0 1 8 0c0 3.5 1.3 4.8 1.3 4.8H4.7S6 11.5 6 8Z" />
      <path d="M8.3 15.5a1.7 1.7 0 0 0 3.4 0" />
    </svg>
  ),
  broadcasts: (
    <svg {...base}>
      <path d="M3 8.5v3l2 .4v4a1 1 0 0 0 2 0v-3.6l7.5 1.7c1 .2 1.5-.4 1.5-1.2V8.2c0-.8-.5-1.4-1.5-1.2L5 8.7 3 8.5Z" />
    </svg>
  ),
  'scheduled-messages': (
    <svg {...base}>
      <circle cx="10" cy="10.5" r="6.5" />
      <path d="M10 6.5v4l3 2" />
    </svg>
  ),
  numbers: (
    <svg {...base}>
      <path d="M6 3.5h2l1 3.5-1.7 1c.7 1.9 2.2 3.4 4.1 4.1l1-1.7 3.5 1v2c0 1.1-1 2-2.1 1.8-6-1-9.6-4.6-10.6-10.6C2.7 4.5 4.9 3.5 6 3.5Z" />
    </svg>
  ),
  team: (
    <svg {...base}>
      <circle cx="7.5" cy="7" r="2.6" />
      <circle cx="14" cy="8" r="2" />
      <path d="M3 16c0-2.6 2-4.3 4.5-4.3S12 13.4 12 16" />
      <path d="M13 12.2c1.9.3 3 1.6 3 3.8" />
    </svg>
  ),
  agents: (
    <svg {...base}>
      <path d="M10 3l1.4 3.6L15 8l-3.6 1.4L10 13l-1.4-3.6L5 8l3.6-1.4L10 3Z" />
      <path d="M15.5 13l.6 1.6L17.7 15l-1.6.6-.6 1.6-.6-1.6-1.6-.6 1.6-.6.5-1.6Z" />
    </svg>
  ),
  groups: (
    <svg {...base}>
      <rect x="3" y="3" width="6.5" height="6.5" rx="1.5" />
      <rect x="10.5" y="3" width="6.5" height="6.5" rx="1.5" />
      <rect x="3" y="10.5" width="6.5" height="6.5" rx="1.5" />
      <rect x="10.5" y="10.5" width="6.5" height="6.5" rx="1.5" />
    </svg>
  ),
  'auto-reply': (
    <svg {...base}>
      <path d="M8 6.5 5 9.5l3 3" />
      <path d="M5 9.5h7a3.5 3.5 0 0 1 0 7h-1.5" />
    </svg>
  ),
  knowledge: (
    <svg {...base}>
      <path d="M4 4.5c1.6-.9 3.6-.9 5.2 0v11c-1.6-.9-3.6-.9-5.2 0v-11Z" />
      <path d="M15.2 4.5c-1.6-.9-3.6-.9-5.2 0v11c1.6-.9 3.6-.9 5.2 0v-11Z" />
    </svg>
  ),
  workflows: (
    <svg {...base}>
      <path d="M4 6h5l2 3h5" />
      <path d="M4 14h5l2-3h5" />
      <path d="M13.5 4.5 16 6l-2.5 1.5M13.5 15.5 16 14l-2.5-1.5" />
    </svg>
  ),
  automation: (
    <svg {...base}>
      <circle cx="10" cy="10" r="2.4" />
      <path d="M10 3.5v2.2M10 14.3v2.2M16.5 10h-2.2M5.7 10H3.5M14.8 5.2l-1.5 1.5M6.7 13.3l-1.5 1.5M14.8 14.8l-1.5-1.5M6.7 6.7 5.2 5.2" />
    </svg>
  ),
  integrations: (
    <svg {...base}>
      <path d="M8 4.5h2.2a1.3 1.3 0 0 1 0 2.6H15v3.4h-1.3a1.3 1.3 0 0 0 0 2.6H15v2.4H8v-1.3a1.3 1.3 0 0 0-2.6 0V15H4V8h1.3a1.3 1.3 0 0 0 0-2.6H4V4.5h4Z" />
    </svg>
  ),
  exports: (
    <svg {...base}>
      <path d="M10 3v9.5M6.5 9l3.5 3.5L13.5 9" />
      <path d="M4 15.5h12" />
    </svg>
  ),
  'audit-log': (
    <svg {...base}>
      <path d="M10 3 4 5.2v4.4c0 4 2.5 6.5 6 7.4 3.5-.9 6-3.4 6-7.4V5.2L10 3Z" />
      <path d="M7.3 10 9.2 12l3.5-4" />
    </svg>
  ),
  sso: (
    <svg {...base}>
      <rect x="5" y="9" width="10" height="7" rx="1.5" />
      <path d="M7.2 9V6.8a2.8 2.8 0 0 1 5.6 0V9" />
    </svg>
  ),
  settings: (
    <svg {...base}>
      <circle cx="10" cy="10" r="2.6" />
      <path d="M10 4v1.6M10 14.4V16M16 10h-1.6M5.6 10H4M14.2 5.8l-1.1 1.1M6.9 13.1l-1.1 1.1M14.2 14.2l-1.1-1.1M6.9 6.9 5.8 5.8" />
    </svg>
  ),
  billing: (
    <svg {...base}>
      <rect x="3" y="5.5" width="14" height="9.5" rx="1.6" />
      <path d="M3 8.5h14" />
      <path d="M6 12h3" />
    </svg>
  ),
}
